import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import BaseController from '../BaseController';
import { Inject } from '@odinjs/odin';
import { ChecklistAPI, ChecklistItem, DeckAPI, DeckValue, FetchAPI, FilterAPI } from '@philips-internal/oswald-ui-components';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import moment from 'moment';
import { ClickEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import UserContext from '../../utils/user-context';
import { Value } from '@philips-internal/oswald-ui-core';
import { applicationContext, foundationContext } from '@philips-internal/oswald-ui-foundation';
import { FetchEvent, RenderEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import React, { ReactElement } from 'react';
import { SituationType, StatusGroup } from '../../constants/payment-status';
import { statusPaymentGroupMap } from '../../constants/application-constants';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { FragmentNames, PaymentListAliases } from '../../codegen';
import { CardPaymentProps } from '@philips-emr/patient-components/src/components/card-payment/card-payment-type';

interface PaymentFilter {
  filterFromDate?: string;
  filterPaymentStatus?: Situation[];
}
interface Situation {
  key: string;
  label: string;
  value: string;
}

const situations: ChecklistItem[] = [
  { 'key':'1', 'label':MessageService.translate('patient.opened'), 'value':false},
  { 'key':'2,4,6', 'label':MessageService.translate('patient.filtered-paid-status-checkbox'), 'value':false },
  { 'key':'3', 'label':MessageService.translate('patient.canceled'), 'value':false },
  { 'key':'5', 'label': MessageService.translate('patient.transferred'), 'value': false },
  { 'key':'7', 'label': MessageService.translate('patient.late'), 'value': false }
];
@Controller({ component: PaymentListAliases.LAYOUT_PAYMENT, fragment: FragmentNames.PAYMENT_LIST })
class Payment extends BaseController {

  internalNavigation!: boolean;

  @Inject(PaymentListAliases.FILTER_PAYMENTS_LIST )
    filterListApi!: FilterAPI;

  @Inject(PaymentListAliases.FILTER_FIELD_PAYMENT_STATUS)
    filterPaymentStatusApi!: FilterAPI;

  @Inject(PaymentListAliases.DECK_PAYMENTS_LIST)
    deckPaymentListApi!: DeckAPI;

  @Inject(PaymentListAliases.FETCH_PAYMENT)
    fetchPaymentListApi!: FetchAPI;

  @Inject(PaymentListAliases.CHECKLIST_STATUS)
    checklistSituationsApi!: ChecklistAPI;

  @OnMount({ target: PaymentListAliases.FILTER_PAYMENTS_LIST })
  handleFilterMount(): void {
    const filterFromDate = moment().format(ISO_DATE_FORMAT);
    this.context.set({ initialValueDate: filterFromDate, initialValueStatus: situations });

    const record = {
      filterFromDate: filterFromDate,
      filterPaymentStatus: situations
    };

    this.filterListApi.setData(record);
    this.filterListApi.submit();
  }
  @OnSubmit({target: PaymentListAliases.FILTER_PAYMENTS_LIST })
  handleSubmit(event: SubmitEvent): void {
    const initialValueDate = this.context.get('initialValueDate');
    const initialValueStatus = this.context.get('initialValueStatus');
    const { patientId } = UserContext;
    const paymentFilter = event.record as PaymentFilter;
    const filterPaymentStatus: string[] = [];

    let  body = this.addValueToBodyFilter('establishmentCode', foundationContext.getEstablishment()?.id, {});
    body = this.addValueToBodyFilter('periodType', 'E', body);
    body = this.addValueToBodyFilter('personCode', patientId.toString() , body);

    if (paymentFilter?.filterFromDate || initialValueDate) {
      const date = paymentFilter?.filterFromDate || initialValueDate as string
      const startDate = moment(date, ISO_DATE_FORMAT).subtract(60, 'd').format(ISO_DATE_FORMAT);
      body = this.addValueToBodyFilter('startDateFormat', startDate, body);
      body = this.addValueToBodyFilter('endDateFormat', moment(date, ISO_DATE_FORMAT).format(ISO_DATE_FORMAT), body);
    }

    if (paymentFilter?.filterPaymentStatus || initialValueStatus) {
      const situation = paymentFilter?.filterPaymentStatus || initialValueStatus as Situation[]
      situation?.forEach((element: Situation) => filterPaymentStatus.push(element.key));
      body = this.addValueToBodyFilter('situationType', filterPaymentStatus, body);
    }

    this.context.set({
      params: {
        ...body,
        ...paymentFilter,
      }
    });

    this.filterListApi.persistState();
    this.fetchPaymentListApi.setParameters(body);
    this.fetchPaymentListApi.forceFetch();
  }

  private addValueToBodyFilter(field: string, value: string | undefined | string[], body: Record<string, Value>): Record<string, Value> {
    if (value) {
      return { ...body, [field]: value };
    }
    return body;
  }

  @OnMount({ target: PaymentListAliases.CHECKLIST_STATUS })
  onChecklistMount(): void {
    const { checklistSituationsApi } = this;
    checklistSituationsApi.setData(situations);
  }

  @OnAfterFetch({ target: PaymentListAliases.FETCH_PAYMENT })
  onAfterFetchPaymentList(event: FetchEvent): void {
    const cardList = event.data as CardPaymentProps[];
    const deckData  = cardList.map(cardInfo => {
      if (cardInfo.status === SituationType.PAID) {
        cardInfo.statusGroup = StatusGroup.PAID;
      } else{
        cardInfo.statusGroup = StatusGroup.PAYABLE;
      }
      return cardInfo;
    });
    this.deckPaymentListApi.setData(deckData as unknown as DeckValue[]);
  }

  @OnDynamicContentRender({ target: PaymentListAliases.DECK_PAYMENTS_LIST })
  handleDeckGroupRender(event: RenderEvent): void {
    if (event) {
      const { render } = event;
      const data = event.data as CardPaymentProps[];
      const status = MessageService.translate(statusPaymentGroupMap.get(data[0].statusGroup) || '');
      const renderSituationType: () => ReactElement = () => {
        return(
          <div className='c-deck__group-header-default-content'>
            <span>{status}</span>
          </div>
        );
      }
      render(renderSituationType);
    }
  }

  @OnClick({ target: PaymentListAliases.CARD_PAYMENT })
  handleCardClick(event: ClickEvent): void {
    if (event && event.value) {
      const { accountNumber } = event.value;
      applicationContext.redirect(APPLICATION_ROUTES.ONLINE_PAYMENTS_DETAIL, {accountNumber: accountNumber});
    }
  }
}

export default Payment;